const FROZEN_DISPATCHER_STATUS_CODES = Object.freeze([
  "frozen",
  "frozen_prefix",
  "frozen_shared_loading",
]);

const IMPLICIT_FROZEN_DISPATCHER_STATUS_CODES = Object.freeze([
  "frozen_prefix",
  "frozen_shared_loading",
]);

function normalizeDispatcherFreezeStatusCode(code) {
  return typeof code === "string" ? code.trim().toLowerCase() : "";
}

export function isFrozenDispatcherStatusCode(code) {
  return FROZEN_DISPATCHER_STATUS_CODES.includes(normalizeDispatcherFreezeStatusCode(code));
}

export function isImplicitFrozenDispatcherStatusCode(code) {
  return IMPLICIT_FROZEN_DISPATCHER_STATUS_CODES.includes(normalizeDispatcherFreezeStatusCode(code));
}

function normalizeFreezeId(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function collectTripConsecutiveLoadingOrderGroups(trip) {
  const steps = Array.isArray(trip?.operational_steps) ? trip.operational_steps : [];
  const groups = [];
  let index = 0;
  while (index < steps.length) {
    const step = steps[index];
    if (String(step?.step_type || "").trim().toLowerCase() !== "loading") {
      index += 1;
      continue;
    }
    const orderIds = [];
    const seenOrderIds = new Set();
    while (index < steps.length) {
      const loadingStep = steps[index];
      if (String(loadingStep?.step_type || "").trim().toLowerCase() !== "loading") {
        break;
      }
      const orderId = normalizeFreezeId(loadingStep?.order_id);
      if (orderId && !seenOrderIds.has(orderId)) {
        seenOrderIds.add(orderId);
        orderIds.push(orderId);
      }
      index += 1;
    }
    if (orderIds.length) {
      groups.push(orderIds);
    }
  }
  return groups;
}

function resolveChainMemberStatus(step, relatedClient = null) {
  const executionStatus = String(
    step?.dispatcher_execution_status || relatedClient?.dispatcher_execution_status || "",
  ).trim().toLowerCase();
  const kind = String(
    step?.dispatcher_frozen_kind || relatedClient?.dispatcher_frozen_kind || "",
  ).trim().toLowerCase();
  const isFrozen = Boolean(step?.is_dispatcher_frozen || relatedClient?.is_dispatcher_frozen);
  if (executionStatus === "frozen") {
    return "frozen";
  }
  if (executionStatus === "delivered") {
    return "delivered";
  }
  if (isFrozen && kind === "shared_loading") {
    return "frozen_shared_loading";
  }
  if (isFrozen) {
    return "frozen_prefix";
  }
  return "";
}

function isSharedLoadingChainMemberStatus(status) {
  return status === "frozen"
    || status === "delivered"
    || status === "frozen_prefix"
    || status === "frozen_shared_loading";
}

function isPortalConfirmedLoadingStep(step) {
  const isConfirmed = Boolean(step?.is_confirmed || step?.is_loading_confirmed);
  if (!isConfirmed) {
    return false;
  }
  const confirmationPortail = String(step?.confirmation_portail || "").trim().toLowerCase();
  const confirmationSource = String(step?.confirmation_source || "").trim().toLowerCase();
  return confirmationPortail === "confirmée"
    || confirmationPortail === "confirmee"
    || confirmationSource === "driver_portal";
}

export function resolveSharedLoadingFreezeChains(trip) {
  const steps = Array.isArray(trip?.operational_steps) ? trip.operational_steps : [];
  const clients = Array.isArray(trip?.clients) ? trip.clients : [];
  const clientsByOrderId = new Map();
  const clientsByDeliveryId = new Map();
  for (const client of clients) {
    const orderId = normalizeFreezeId(client?.order_id);
    const deliveryId = normalizeFreezeId(client?.delivery_id);
    if (orderId) {
      clientsByOrderId.set(orderId, client);
    }
    if (deliveryId) {
      clientsByDeliveryId.set(deliveryId, client);
    }
  }

  const deliverySteps = steps.filter((step) => String(step?.step_type || "").trim().toLowerCase() === "delivery");
  const chains = [];

  for (const blockOrderIds of collectTripConsecutiveLoadingOrderGroups(trip)) {
    const blockSet = new Set(blockOrderIds);
    const blockDeliveries = deliverySteps.filter((step) => blockSet.has(normalizeFreezeId(step?.order_id)));
    const hasPortalLoadingAnchor = steps.some((step) => (
      String(step?.step_type || "").trim().toLowerCase() === "loading"
      && blockSet.has(normalizeFreezeId(step?.order_id))
      && isPortalConfirmedLoadingStep(step)
    ));
    const members = [];
    const seenMembers = new Set();
    const anchorDeliveryIds = [];
    const seenAnchors = new Set();

    for (const step of blockDeliveries) {
      const deliveryId = normalizeFreezeId(step?.delivery_id);
      const orderId = normalizeFreezeId(step?.order_id);
      const client = clientsByDeliveryId.get(deliveryId) || clientsByOrderId.get(orderId) || null;
      const status = resolveChainMemberStatus(step, client);
      const projectedAnchors = step?.dispatcher_frozen_anchor_delivery_ids
        || client?.dispatcher_frozen_anchor_delivery_ids;
      if (Array.isArray(projectedAnchors)) {
        for (const id of projectedAnchors) {
          const normalized = normalizeFreezeId(id);
          if (normalized && !seenAnchors.has(normalized)) {
            seenAnchors.add(normalized);
            anchorDeliveryIds.push(normalized);
          }
        }
      }
      if (status === "frozen" && deliveryId && !seenAnchors.has(deliveryId)) {
        seenAnchors.add(deliveryId);
        anchorDeliveryIds.push(deliveryId);
      }
      if (isSharedLoadingChainMemberStatus(status) || hasPortalLoadingAnchor) {
        const memberKey = deliveryId || orderId;
        if (memberKey && !seenMembers.has(memberKey)) {
          seenMembers.add(memberKey);
          members.push({
            deliveryId,
            orderId,
            status: status || "frozen_shared_loading",
          });
        }
      }
    }

    if (!members.length) {
      continue;
    }
    const hasLotAnchor = hasPortalLoadingAnchor || members.some((member) => (
      member.status === "frozen"
      || member.status === "delivered"
      || member.status === "frozen_shared_loading"
    ));
    if (!hasLotAnchor) {
      continue;
    }

    const tail = members[members.length - 1];
    const anchorStepIds = [];
    const seenAnchorStepIds = new Set();
    for (const step of blockDeliveries) {
      const deliveryId = normalizeFreezeId(step?.delivery_id);
      const stepId = normalizeFreezeId(step?.step_id);
      if (!deliveryId || !stepId || !anchorDeliveryIds.includes(deliveryId) || seenAnchorStepIds.has(stepId)) {
        continue;
      }
      seenAnchorStepIds.add(stepId);
      anchorStepIds.push(stepId);
    }
    chains.push({
      tailDeliveryId: tail.deliveryId,
      tailOrderId: tail.orderId,
      anchorDeliveryIds,
      anchorStepIds,
      hasSharedLoadingMember: members.some((member) => member.status === "frozen_shared_loading"),
      memberDeliveryIds: members.map((member) => member.deliveryId).filter(Boolean),
      memberOrderIds: members.map((member) => member.orderId).filter(Boolean),
    });
  }

  return chains;
}

export function resolveSharedLoadingFreezeChain(trip, { deliveryId = "", orderId = "", stepType = "" } = {}) {
  if (String(stepType || "").trim().toLowerCase() === "loading") {
    return null;
  }
  const normalizedDeliveryId = normalizeFreezeId(deliveryId);
  const normalizedOrderId = normalizeFreezeId(orderId);
  if (!normalizedDeliveryId && !normalizedOrderId) {
    return null;
  }
  const chain = resolveSharedLoadingFreezeChains(trip).find((candidate) => (
    (normalizedDeliveryId && candidate.memberDeliveryIds.includes(normalizedDeliveryId))
    || (normalizedOrderId && candidate.memberOrderIds.includes(normalizedOrderId))
  ));
  if (!chain) {
    return null;
  }
  const isTail = Boolean(
    (normalizedDeliveryId && chain.tailDeliveryId === normalizedDeliveryId)
    || (!normalizedDeliveryId && normalizedOrderId && chain.tailOrderId === normalizedOrderId)
  );
  return {
    ...chain,
    isMember: true,
    isTail,
  };
}
